import { useCallback, useEffect, useState } from 'react';
import { useToast } from '../../components/ui';
import { useAuth } from '../../contexts/AuthContext';
import { GetUserContactsStatus, DeleteUserContacts } from '../../api/api';
import { bulkContactsSupported } from '../../utils/contacts';
import { syncPhoneBook } from '../../utils/contactSync';

// "Sync my contacts" on the Profile page — shared by ContactSync.desktop / .mobile.
//
// Google Play allows uploading the phone book ONLY with a prominent in-app
// disclosure the user agrees to first. So: nothing is read or sent until the
// user taps "Agree" on the disclosure below; every upload carries Consent: 1
// (the API refuses without it and stores the consent date); the user can
// delete everything at any time. The rows are visible only to this user and
// are used to find people quickly when adding guests (SearchEvt_People).
// Reading the whole phone book needs the Android app (Capacitor plugin).
export const CONTACT_SYNC_DISCLOSURE = [
  'Mehfil will upload the contacts in your phone book — names, phone numbers, email and company — to your Mehfil account.',
  'They are used only to find people quickly when you add guests. Only you can see them; they are never shared or used for anything else.',
  'You can delete all uploaded contacts at any time from this screen.',
];
export function useContactSync(user) {
  const toast = useToast();
  const { isViewingAs } = useAuth();
  // Never while a Super Admin views as someone else: it would put the admin's
  // phone book into that user's account.
  const canSync = bulkContactsSupported() && !isViewingAs;
  const [status, setStatus] = useState(null);       // { contacts, numbers, syncedDate, consentDate }
  const [consentOpen, setConsentOpen] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [deleting, setDeleting] = useState(false);

  const loadStatus = useCallback(() => {
    if (!user?.UserId) return;
    GetUserContactsStatus({ UserId: user.UserId }).then(setStatus).catch(() => setStatus(null));
  }, [user]);
  useEffect(() => { loadStatus(); }, [loadStatus]);

  // Agreed before (consent date on the server) → sync straight away; else show the disclosure.
  const askConsent = () => (status?.consentDate ? agreeAndSync() : setConsentOpen(true));
  const decline = () => setConsentOpen(false);

  // Only runs after "Agree": read the phone book, compare with the server's
  // fingerprints, upload only new / changed contacts (utils/contactSync).
  const agreeAndSync = async () => {
    setConsentOpen(false);
    setSyncing(true);
    try {
      const r = await syncPhoneBook(user.UserId, { prompt: true, onProgress: setProgress });
      toast(`${r.total} contacts checked · ${r.added} new · ${r.updated} updated · ${r.unchanged} unchanged${r.defaults ? ` · ${r.defaults} ★ default numbers marked` : ''}`);
    } catch (e) {
      toast(e?.message || 'Contact sync failed');
    } finally {
      setSyncing(false);
      loadStatus();
    }
  };

  const deleteAll = async () => {
    if (!window.confirm('Delete all contacts you uploaded to Mehfil?')) return;
    setDeleting(true);
    try {
      const r = await DeleteUserContacts({ UserId: user.UserId });
      toast(`Deleted ${r?.deleted || 0} saved numbers`);
    } catch {
      toast('Could not delete - try again');
    } finally {
      setDeleting(false);
      loadStatus();
    }
  };

  return {
    canSync, status, consentOpen, syncing, progress, deleting, disclosure: CONTACT_SYNC_DISCLOSURE,
    askConsent, decline, agreeAndSync, deleteAll,
  };
}
